const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/analytics/events`;
const FLUSH_INTERVAL = 5000;
const BATCH_SIZE = 10;

let queue = [];
let flushTimer = null;
let scrollMax = 0;
let currentPage = null;
let pageEnteredAt = null;

function getSid() {
  try {
    let sid = sessionStorage.getItem("mise-track-sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("mise-track-sid", sid);
    }
    return sid;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2);
  }
}

function getUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || undefined,
      utm_medium: params.get("utm_medium") || undefined,
      utm_campaign: params.get("utm_campaign") || undefined,
    };
  } catch {
    return {};
  }
}

function push(evt) {
  if (!BACKEND_URL) return;
  queue.push(evt);
  if (queue.length >= BATCH_SIZE) flush();
}

function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 50);
  const payload = JSON.stringify({
    events: batch,
    ua: navigator.userAgent,
  });
  if (navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
  } else {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  }
}

function base() {
  return {
    sid: getSid(),
    ts: Date.now(),
    page: window.location.pathname,
    vw: window.innerWidth,
    vh: window.innerHeight,
    lang: document.documentElement.lang || navigator.language?.slice(0, 2) || undefined,
  };
}

export function trackPageview() {
  if (window.location.pathname.startsWith("/admin")) return;
  sendScrollAndDuration();
  currentPage = window.location.pathname;
  pageEnteredAt = Date.now();
  scrollMax = 0;
  const utm = getUtm();
  push({
    ...base(),
    type: "pageview",
    referrer: document.referrer || undefined,
    ...utm,
  });
}

export function trackClick(label) {
  if (window.location.pathname.startsWith("/admin")) return;
  push({ ...base(), type: "click", label });
}

export function trackFormAction(label) {
  if (window.location.pathname.startsWith("/admin")) return;
  push({ ...base(), type: "form_action", label });
}

function sendScrollAndDuration() {
  if (!currentPage || window.location.pathname.startsWith("/admin")) return;
  if (scrollMax > 0) {
    push({ ...base(), page: currentPage, type: "scroll", value: scrollMax });
  }
  if (pageEnteredAt) {
    const dur = (Date.now() - pageEnteredAt) / 1000;
    if (dur > 1) {
      push({ ...base(), page: currentPage, type: "session_end", value: Math.round(dur * 10) / 10 });
    }
  }
}

function onScroll() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight > 0) {
    const pct = Math.round((scrollTop / docHeight) * 100);
    if (pct > scrollMax) scrollMax = pct;
  }
}

// Where an outgoing link leads: "host/path" for web links (no query string), "mailto:address" for email.
function outboundTarget(a) {
  const href = a.getAttribute("href") || "";
  if (href.startsWith("mailto:")) return "mailto:" + href.slice(7).split("?")[0].toLowerCase();
  try {
    const url = new URL(href, window.location.href);
    if (!/^https?:$/.test(url.protocol) || url.host === window.location.host) return null;
    return url.hostname.replace(/^www\./, "") + url.pathname.replace(/\/$/, "");
  } catch {
    return null;
  }
}

export function trackOutbound(target) {
  if (window.location.pathname.startsWith("/admin")) return;
  push({ ...base(), type: "outbound", label: target });
}

function onClickDelegate(e) {
  const el = e.target.closest("[data-track]");
  if (el) trackClick(el.getAttribute("data-track"));
  const a = e.target.closest("a[href]");
  const target = a && outboundTarget(a);
  if (target) {
    trackOutbound(target);
    flush(); // the visitor may be leaving this page
  }
}

export function initTracker() {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/admin")) return;
  if (!BACKEND_URL) return;

  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("click", onClickDelegate, { capture: true });

  window.addEventListener("beforeunload", () => {
    sendScrollAndDuration();
    flush();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendScrollAndDuration();
      flush();
    }
  });

  flushTimer = setInterval(flush, FLUSH_INTERVAL);

  trackPageview();
}

export function destroyTracker() {
  if (flushTimer) clearInterval(flushTimer);
  window.removeEventListener("scroll", onScroll);
  document.removeEventListener("click", onClickDelegate, { capture: true });
  sendScrollAndDuration();
  flush();
}
